import { ChangeDetectorRef, Component, effect, inject, signal, ViewChild } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Style as StatusBarStyle, StatusBar } from '@capacitor/status-bar';
import { filter } from 'rxjs/operators';
import { AlertController, IonApp, IonContent, IonRouterOutlet, Platform } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  add,
  addCircle,
  addCircleOutline,
  addOutline,
  alarmOutline,
  arrowBack,
  arrowBackSharp,
  barbellOutline,
  bodyOutline,
  cafe,
  calendarNumberOutline,
  calendarOutline,
  caretBackCircle,
  chatbubbles,
  checkmark,
  checkmarkCircle,
  checkboxOutline,
  checkmarkCircleOutline,
  checkmarkOutline,
  chevronBack,
  chevronDownCircle,
  chevronDownOutline,
  chevronExpand,
  chevronForward,
  chevronForwardOutline,
  chevronUp,
  close,
  closeCircle,
  closeCircleSharp,
  closeOutline,
  cloudDoneOutline,
  alertCircleOutline,
  cloudDownloadOutline,
  cloudOfflineOutline,
  cloudOutline,
  cloudUploadOutline,
  colorPaletteOutline,
  businessOutline,
  cardOutline,
  copyOutline,
  createOutline,
  cropOutline,
  returnUpBack,
  returnUpForward,
  diamondOutline,
  documentAttachOutline,
  documentTextOutline,
  downloadOutline,
  ellipse,
  ellipsisVertical,
  eyeOutline,
  flame,
  gitNetworkOutline,
  globeOutline,
  happyOutline,
  helpBuoyOutline,
  helpCircleOutline,
  homeOutline,
  imageOutline,
  informationCircleOutline,
  layersOutline,
  libraryOutline,
  locationOutline,
  lockClosedOutline,
  logInOutline,
  logOutOutline,
  moonOutline,
  mic,
  micOutline,
  musicalNote,
  musicalNotes,
  musicalNotesOutline,
  speedometer,
  pause,
  pauseCircle,
  pauseCircleOutline,
  pencilOutline,
  mailOpenOutline,
  mailUnreadOutline,
  personOutline,
  phonePortraitOutline,
  play,
  playBack,
  playCircle,
  playCircleOutline,
  playForward,
  playOutline,
  playSkipBack,
  playSkipForward,
  pricetags,
  repeat,
  repeatOutline,
  search,
  searchOutline,
  searchSharp,
  settings,
  scanOutline,
  settingsOutline,
  shareOutline,
  shareSocialOutline,
  shieldCheckmarkOutline,
  speedometerOutline,
  star,
  starOutline,
  statsChartOutline,
  stopCircle,
  sunnyOutline,
  syncOutline,
  stopCircleOutline,
  timeOutline,
  todayOutline,
  trash,
  trashOutline,
  pencil,
  documentText,
  swapHorizontal,
  refreshOutline,
  albumsOutline,
  arrowBackOutline,
  arrowForwardOutline,
  arrowUndoOutline,
  cartOutline,
  chevronBackOutline,
  chevronDown,
  closeCircleOutline,
  compassOutline,
  home,
  imagesOutline,
  keyOutline,
  lockClosed,
  logoApple,
  logoGoogle,
  logoInstagram,
  mailOutline,
  peopleOutline,
  personAddOutline,
  pulseOutline,
  readerOutline,
  reorderThreeOutline,
  saveOutline,
  sparklesOutline,
  send,
  shieldOutline,
  videocam,
  videocamOutline,
  volumeMuteOutline,
  walkOutline,
  warningOutline,
} from 'ionicons/icons';
import { AppConfigService } from '../common/config/app.config.service';
import { ComponentAccess } from '../common/decorators/component-access.decorator';
import { LoadingService } from '../common/services/loading/loading.service';
import { LogFactory } from '../common/services/logger/log-factory';
import { VersionGateService } from './core/version-gate/version-gate.service';
import { AppRefreshCoordinator } from './core/visibility/app-refresh-coordinator.service';
import { AppVisibilityService } from './core/visibility/app-visibility.service';
import { RefreshKey } from './core/visibility/refresh-key.enum';
import { CountryAvailabilityService } from '../common/services/country-availability/country-availability.service';
import { FitpackOpenService } from './features/sequal/services/fitpack-open.service';
import { ShareLinkOpenService } from './features/sequal/services/share-link-open.service';
import { PlanReminderService } from './features/sequal/services/plan-reminder.service';
import { SilentStretchService } from './features/sequal/services/silent-stretch.service';
import { StorageRecoveryService } from './features/sequal/services/storage-recovery.service';
import { WorkoutRemoteControlService } from './features/sequal/services/workout-remote-control.service';
import { MenuComponent } from './components/menu/menu.component';
import { OnboardingHostComponent } from './features/sequal/onboarding/onboarding-host.component';
import { TourOverlayComponent } from './features/sequal/onboarding/tour-overlay.component';
import { TourService } from './features/sequal/onboarding/tour.service';
import { TabsShellService } from './features/sequal/tabs/tabs-shell.service';
import { SequalSplashComponent } from './components/splash/splash.component';
import { ComingSoonComponent } from './core/pages/coming-soon/coming-soon.component';
import { EmailGateComponent } from './core/pages/email-gate/email-gate.component';
import { PaywallHostComponent } from './core/payment/components/paywall-host/paywall-host.component';
import { SubscriptionService } from './core/payment/services/subscription.service';
import { PurchaseRecoveryService } from './core/payment/services/purchase-recovery.service';
import { PaymentProgressService } from './core/payment/services/payment-progress.service';
import { DeviceStorageService } from './core/service/device-storage.service';
import { ThemeService } from './core/service/theme.service';
import { WorkoutCommandCenterComponent } from './features/sequal/components/workout-command-center/workout-command-center.component';
import { ThemeService as SequalThemeService } from './features/sequal/services/theme.service';
import { WorkoutService } from './features/sequal/services/workout.service';
import { SyncService } from './features/sequal/sync/sync.service';

// Focus and visibility can fire together when the user returns from a VPN
// client. Coalesce that pair while still making a genuine later return perform
// a fresh server-side country check.
const WEB_COUNTRY_REFRESH_COALESCE_MS = 1_000;

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: true,
  imports: [
    IonApp,
    IonRouterOutlet,
    IonContent,
    MenuComponent,
    SequalSplashComponent,
    ComingSoonComponent,
    EmailGateComponent,
    OnboardingHostComponent,
    TourOverlayComponent,
    WorkoutCommandCenterComponent,
    PaywallHostComponent,
  ],
})
@ComponentAccess('AppComponent')
export class AppComponent {
  loadingService = inject(LoadingService);
  private readonly configService = inject(AppConfigService);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly deviceStorage = inject(DeviceStorageService);
  // Eagerly construct the Sequal ThemeService so the saved color-mode + color-theme
  // (light/dark + Grass Green / Ocean Blue / …) are applied at app boot. Without
  // this it only ran when the Settings page lazy-loaded, so the app flashed the
  // raw :root pink fallback until the user opened Settings.
  private readonly sequalThemeService = inject(SequalThemeService);
  private readonly workoutService = inject(WorkoutService);
  // Constructed at boot so its constructor subscribes to auth state and pulls
  // Pro entitlement once the user is authenticated (see SubscriptionService).
  private readonly subscription = inject(SubscriptionService);
  // Books store payments that were taken but never booked (ADR-0307), on
  // boot + resume and the moment a late approval arrives.
  private readonly purchaseRecovery = inject(PurchaseRecoveryService);
  // The store payment being booked right now, if any (ADR-0479). Android Back
  // must close its popup, and never exit the app from under it.
  private readonly paymentProgress = inject(PaymentProgressService);
  /** Brings the account's cloud library down on resume — see ADR-0256. */
  private readonly sync = inject(SyncService);
  private readonly countryAvailability = inject(CountryAvailabilityService);
  // Server-driven version gate — checks build vs policy on launch, and on a
  // resume once aged, and prompts/blocks as needed (Item 2). No-op on web.
  private readonly versionGate = inject(VersionGateService);
  // Decides what is re-fetched when the app comes back (ADR-0264). Constructed
  // here, at boot, so it is listening before the first resume.
  private readonly refreshCoordinator = inject(AppRefreshCoordinator);
  private readonly visibility = inject(AppVisibilityService);
  // Plan reminders (ADR-0055) — sliding 14-day window of local notifications,
  // topped up at boot (and on every schedule change from the program page).
  private readonly planReminder = inject(PlanReminderService);
  // Registers the OS "open .fitpack" handlers so a shared collection tapped in
  // Files / an email / a chat lands straight in import review (native-only).
  private readonly fitpackOpen = inject(FitpackOpenService);
  private readonly shareLinkOpen = inject(ShareLinkOpenService);
  // The workout outside the app (ADR-0284, ADR-0285): the iPhone lock screen,
  // the Android notification's buttons, and a locked iPhone's silent stretches.
  private readonly workoutRemote = inject(WorkoutRemoteControlService);
  private readonly silentStretch = inject(SilentStretchService);
  // Reloads the app when iOS drops its on-device database under a suspended app.
  private readonly storageRecovery = inject(StorageRecoveryService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly platform = inject(Platform);
  private readonly alertCtrl = inject(AlertController);
  private readonly tour = inject(TourService);
  // Knows the tab shell's nested outlet — Back walks that stack before the app
  // is allowed to exit (see handleHardwareBack).
  private readonly tabsShell = inject(TabsShellService);
  private lastWebCountryRefreshAt = 0;
  private webCountryRefreshInFlight = false;

  /** Root navigation outlet — used by the Android hardware-back handler to pop
   *  the current page (or exit the app when there's nothing left to pop). */
  @ViewChild(IonRouterOutlet) private routerOutlet?: IonRouterOutlet;

  /** Mirrors workout$ — drives whether the player command-center renders. */
  protected readonly panelOpen = signal<boolean>(false);
  /** True when the command-center is collapsed to its mini bar. */
  protected readonly panelMinimized = signal<boolean>(false);

  readonly isComingSoon: boolean;
  // Animated splash is native-only AND opt-in via env config
  // (app.animatedSplash). Disabled → app launches immediately, saving the
  // ~5s splash hold.
  protected readonly showSplash = signal<boolean>(
    Capacitor.isNativePlatform() && this.configService.isAnimatedSplashEnabled,
  );

  private readonly logger = inject(LogFactory).createLogger(AppComponent);

  constructor() {
    this.isComingSoon = this.configService.isComingSoon;

    this.initStatusBar();
    this.initHardwareBackButton();
    this.initMediaSaveGuard();

    // Ask the browser not to evict this origin. Runs before anything is signed
    // in because a guest or free-tier library has no cloud copy — an eviction
    // is the whole library. Fire-and-forget; a refusal only means we're exposed.
    void this.deviceStorage.requestPersistence();

    // Kick off the version gate: the build number (already loaded by the app
    // initializer; memoised), then check the policy. Fire-and-forget; failures fail open. Launch fetches go through the
    // coordinator so they count as fresh and a quick resume does not repeat them.
    // Book any store payment this device holds unbooked - once the session is
    // known, and again on sign-in - and listen for late approvals (ADR-0307).
    // Native, signed-in only; no request unless the store holds one. Fails
    // open - the server's store notification is the net.
    this.purchaseRecovery.start();
    void this.versionGate
      .initialize()
      .then(() => this.refreshCoordinator.forceRefresh(RefreshKey.VersionGate));

    // Ask whether purchasing may be shown on this channel (ADR-0173). Runs before
    // sign-in because the paywall is guest-reachable (App Store 3.1.2(c)). Fails
    // CLOSED, unlike the version gate: a cold start that cannot reach the api
    // hides purchase UI rather than risk rendering a price in a silenced store.
    void this.refreshCoordinator.forceRefresh(RefreshKey.PurchaseAvailability);

    // Same shape, same reasoning, for community publishing: the switch is
    // server-owned, so the app asks instead of shipping a build-time copy of it.
    // Also fails CLOSED — until the server says publishing is live, no publish
    // control is drawn anywhere.
    void this.refreshCoordinator.forceRefresh(RefreshKey.PublishingAvailability);

    // Same again for in-app support: the switch is the api's, and this fails
    // CLOSED — until the server says support is on, no entry point is drawn.
    void this.refreshCoordinator.forceRefresh(RefreshKey.SupportAvailability);

    // Top up scheduled plan reminders (native no-op on web; best-effort).
    void this.planReminder.reschedule();

    // Handle a .fitpack the OS opened into the app (tap in Files/email/chat, or
    // the share sheet). Native-only; also drains the cold-start launch URL.
    void this.fitpackOpen.initialize();

    // Shared links and scanned codes: https://<landing>/l?id= (a listing) or
    // ?publisher= (a publisher). Native-only; also drains the cold-start launch
    // URL, so a link tapped while the app was closed still lands somewhere.
    void this.shareLinkOpen.initialize();

    // The minimized workout mini-player floats above the tab bar and overlaps
    // the bottom of page content. Toggle a body class so global.scss can pad
    // every scroll area, keeping the last items of long pages reachable.
    effect(() => {
      const miniBarVisible = this.panelOpen() && this.panelMinimized();
      document.body.classList.toggle('has-mini-player', miniBarVisible);
    });

    // App-level icons used directly in templates.
    // Also register icons that Ionic standalone components reference
    // internally (ion-select chevron, ion-back-button caret, ion-searchbar
    // search/clear, ion-refresher arrow). Without these, components throw
    // "Failed to construct 'URL': Invalid base URL" because the icon name
    // resolves to a path that's never registered.
    addIcons({
      // app icons
      search,
      pricetags,
      chatbubbles,
      settings,
      close,
      add,
      checkmark,
      cafe,
      mic,
      'musical-notes': musicalNotes,
      pause,
      play,
      speedometer,
      'pause-circle': pauseCircle,
      'pause-circle-outline': pauseCircleOutline,
      repeat,
      videocam,
      'add-circle': addCircle,
      'arrow-back': arrowBack,
      'chevron-up': chevronUp,
      'ellipsis-vertical': ellipsisVertical,
      'musical-note': musicalNote,
      'play-back': playBack,
      'play-forward': playForward,
      'play-skip-back': playSkipBack,
      'play-skip-forward': playSkipForward,
      'stop-circle': stopCircle,
      // Sequal tabs / FAB / sections
      'home-outline': homeOutline,
      'library-outline': libraryOutline,
      'calendar-outline': calendarOutline,
      'calendar-number-outline': calendarNumberOutline,
      'today-outline': todayOutline,
      'settings-outline': settingsOutline,
      'add-outline': addOutline,
      'add-circle-outline': addCircleOutline,
      'play-circle': playCircle,
      'play-circle-outline': playCircleOutline,
      'play-outline': playOutline,
      'walk-outline': walkOutline,
      'barbell-outline': barbellOutline,
      'body-outline': bodyOutline,
      'layers-outline': layersOutline,
      'git-network-outline': gitNetworkOutline,
      'time-outline': timeOutline,
      'create-outline': createOutline,
      'crop-outline': cropOutline,
      'return-up-back': returnUpBack,
      'return-up-forward': returnUpForward,
      'trash-outline': trashOutline,
      trash,
      pencil,
      'document-text': documentText,
      'swap-horizontal': swapHorizontal,
      'refresh-outline': refreshOutline,
      'share-outline': shareOutline,
      'share-social-outline': shareSocialOutline,
      'lock-closed-outline': lockClosedOutline,
      'diamond-outline': diamondOutline,
      // Progress dashboard (ADR-0055)
      flame,
      'stats-chart-outline': statsChartOutline,
      'image-outline': imageOutline,
      'happy-outline': happyOutline,
      'help-circle-outline': helpCircleOutline,
      'alarm-outline': alarmOutline,
      'musical-notes-outline': musicalNotesOutline,
      'mic-outline': micOutline,
      'videocam-outline': videocamOutline,
      'volume-mute-outline': volumeMuteOutline,
      'scan-outline': scanOutline,
      'speedometer-outline': speedometerOutline,
      'repeat-outline': repeatOutline,
      'stop-circle-outline': stopCircleOutline,
      'pencil-outline': pencilOutline,
      'copy-outline': copyOutline,
      'albums-outline': albumsOutline,
      'arrow-back-outline': arrowBackOutline,
      'arrow-forward-outline': arrowForwardOutline,
      'arrow-undo-outline': arrowUndoOutline,
      'cart-outline': cartOutline,
      'chevron-back-outline': chevronBackOutline,
      'chevron-down': chevronDown,
      'close-circle-outline': closeCircleOutline,
      'compass-outline': compassOutline,
      home,
      'images-outline': imagesOutline,
      'key-outline': keyOutline,
      'lock-closed': lockClosed,
      'globe-outline': globeOutline,
      'logo-apple': logoApple,
      'logo-google': logoGoogle,
      'logo-instagram': logoInstagram,
      'mail-outline': mailOutline,
      'people-outline': peopleOutline,
      'person-add-outline': personAddOutline,
      'pulse-outline': pulseOutline,
      'reorder-three-outline': reorderThreeOutline,
      'save-outline': saveOutline,
      send,
      'shield-outline': shieldOutline,
      'warning-outline': warningOutline,
      'checkbox-outline': checkboxOutline,
      'checkmark-outline': checkmarkOutline,
      'checkmark-circle-outline': checkmarkCircleOutline,
      'chevron-forward-outline': chevronForwardOutline,
      // Sequal settings page
      'log-in-outline': logInOutline,
      'log-out-outline': logOutOutline,
      // Both halves of the pair: a rating that is only ever drawn hollow cannot
      // show what somebody actually gave. An unregistered name renders nothing
      // at all, so the filled star was simply absent wherever it was used.
      star,
      'star-outline': starOutline,
      'cloud-outline': cloudOutline,
      'cloud-done-outline': cloudDoneOutline,
      'cloud-download-outline': cloudDownloadOutline,
      'cloud-offline-outline': cloudOfflineOutline,
      'alert-circle-outline': alertCircleOutline,
      'cloud-upload-outline': cloudUploadOutline,
      'sync-outline': syncOutline,
      'sparkles-outline': sparklesOutline,
      'color-palette-outline': colorPaletteOutline,
      'business-outline': businessOutline,
      'moon-outline': moonOutline,
      'sunny-outline': sunnyOutline,
      'phone-portrait-outline': phonePortraitOutline,
      // The enquiries worklist toggles between these two; an unregistered name
      // renders nothing, so the button would have looked broken.
      'mail-open-outline': mailOpenOutline,
      'mail-unread-outline': mailUnreadOutline,
      'person-outline': personOutline,
      'shield-checkmark-outline': shieldCheckmarkOutline,
      'card-outline': cardOutline,
      'location-outline': locationOutline,
      'document-text-outline': documentTextOutline,
      'reader-outline': readerOutline,
      'help-buoy-outline': helpBuoyOutline,
      'information-circle-outline': informationCircleOutline,
      'document-attach-outline': documentAttachOutline,
      'download-outline': downloadOutline,
      'eye-outline': eyeOutline,
      // ion-select / accordion
      'chevron-down-outline': chevronDownOutline,
      'chevron-down-circle': chevronDownCircle,
      'chevron-expand': chevronExpand,
      // ion-back-button (iOS + Material)
      'chevron-back': chevronBack,
      'arrow-back-sharp': arrowBackSharp,
      'chevron-forward': chevronForward,
      // ion-searchbar
      'search-outline': searchOutline,
      'search-sharp': searchSharp,
      'close-circle': closeCircle,
      'close-circle-sharp': closeCircleSharp,
      'close-outline': closeOutline,
      // ion-refresher / ion-checkbox / ion-radio
      'caret-back-circle': caretBackCircle,
      'checkmark-circle': checkmarkCircle,
      ellipse,
    });

    // Mirror WorkoutService state into the component-center panel flags so the
    // player UI renders whenever a workout is active and re-expands when a new
    // step starts.
    // Only an open/close is news here: the state emits on every countdown tick,
    // and marking the root for check each time ran change detection across
    // the app four times a second for the whole workout (ADR-0284).
    this.workoutService.workout$.subscribe(w => {
      const open = !!w;
      if (open === this.panelOpen()) return;
      this.panelOpen.set(open);
      this.cdr.markForCheck();
    });
    this.workoutRemote.start();
    this.silentStretch.start();
    this.storageRecovery.start();
    this.workoutService.workoutStarted$.subscribe(() => {
      this.panelMinimized.set(false);
      this.cdr.markForCheck();
    });

    // Drop focus before each navigation. When Ionic transitions between
    // ion-page elements it marks the previous page `aria-hidden="true"`. If
    // a button (e.g. a tab or menu item) inside that page still has keyboard
    // focus when it gets hidden, browsers correctly warn that focus is
    // trapped inside an aria-hidden subtree. Blurring on NavigationStart
    // hands focus back to <body> before the page is marked hidden.
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => {
        const active = document.activeElement;
        if (active instanceof HTMLElement && active !== document.body) {
          active.blur();
        }
      });

    // Resume work that is NOT in the refresh registry (ADR-0264). Entitlement,
    // purchase/publishing/support availability, the bell and the version gate
    // are scheduled by AppRefreshCoordinator. What stays here is resume-shaped
    // work with no screen reading it:
    //  - library hydration: another device may have added something since we
    //    were last awake (ADR-0256). It rate-limits itself (a 5-minute floor and
    //    an in-flight guard), so most resumes cost nothing.
    //  - the web country recheck, coalesced below, so a VPN switched while away
    //    re-quotes prices.
    this.visibility.resumed.pipe(takeUntilDestroyed()).subscribe(() => {
      void this.purchaseRecovery.runNow();
      void this.sync.hydrateFromCloud();
      if (!Capacitor.isNativePlatform()) this.refreshDetectedWebCountry();
    });
    if (!Capacitor.isNativePlatform()) {
      // Page Visibility does not necessarily change when the browser window
      // loses focus to a desktop VPN client. Focus is therefore the second,
      // complementary signal for an immediate request-time country recheck.
      window.addEventListener('focus', () => this.refreshDetectedWebCountry());
    }
  }

  private refreshDetectedWebCountry(): void {
    const now = Date.now();
    if (
      this.webCountryRefreshInFlight ||
      now - this.lastWebCountryRefreshAt < WEB_COUNTRY_REFRESH_COALESCE_MS
    ) {
      return;
    }

    this.lastWebCountryRefreshAt = now;
    this.webCountryRefreshInFlight = true;
    this.countryAvailability.getAvailability(true).subscribe({
      error: (error) => {
        this.webCountryRefreshInFlight = false;
        this.logger.debug('Detected-country refresh deferred', error);
      },
      complete: () => {
        this.webCountryRefreshInFlight = false;
      },
    });
  }

  protected onSplashDone(): void {
    // The status-bar effect reacts to this signal and recolours the bar from
    // the splash background to the header colour — no explicit call needed.
    this.showSplash.set(false);
  }

  /** Player X button → stop the workout and tear down the panel. */
  protected onClosePanel(): void {
    void this.workoutService.stop();
    this.panelOpen.set(false);
    this.panelMinimized.set(false);
  }

  /** Player chevron → collapse to mini-bar; workout keeps running. */
  protected onMinimizePanel(): void {
    this.panelMinimized.set(true);
  }

  protected reopenPanel(): void {
    this.panelMinimized.set(false);
  }

  // Android hardware/gesture back. Ionic's own default handler was not firing
  // reliably here (the router outlet is nested inside an ion-content), so we
  // register an explicit higher-priority handler. Guided tours and alerts must
  // consume Back before an editor's leave handler can open another alert.
  // Native-only — the backButton event never fires on web.
  private initHardwareBackButton(): void {
    if (!Capacitor.isNativePlatform()) return;
    this.platform.backButton.subscribeWithPriority(102, (processNextHandler: () => void) => {
      void this.dismissTopmostBackLayer(processNextHandler);
    });
    this.platform.backButton.subscribeWithPriority(10, () => {
      void this.handleHardwareBack();
    });
  }

  /**
   * Root-level Back. The app's pages live in the outlet nested inside ion-tabs,
   * so the root outlet only holds the auth/error screens pushed above the tabs
   * shell: pop those first, then let the shell walk its own ladder (tab stack →
   * owning tab root → home tab). The app exits only when the user is on the
   * home tab with nothing left to pop.
   */
  private async handleHardwareBack(): Promise<void> {
    if (this.routerOutlet?.canGoBack()) {
      await this.routerOutlet.pop();
      return;
    }
    if (await this.tabsShell.goBack()) return;
    // Never exit while a store payment is still being booked: the purchase
    // would sit paid but unbooked until the next launch, and Play refunds one
    // left unfinished for three days. Bring the popup back instead, so the
    // buyer sees why the app is staying open.
    if (this.paymentProgress.confirming()) {
      this.paymentProgress.show();
      return;
    }
    await App.exitApp();
  }

  /**
   * Consume Back at the top-most app-owned layer before it reaches an editor.
   * A guided tour is rendered outside Ionic's overlay stack; alerts are not,
   * so they are checked separately. This prevents a tour from blocking an
   * alert's buttons and prevents repeated Back events from stacking prompts.
   */
  private async dismissTopmostBackLayer(processNextHandler: () => void): Promise<void> {
    if (this.tour.active()) {
      this.tour.skip();
      return;
    }

    const alert = await this.alertCtrl.getTop();
    if (alert) {
      // An alert that opted out of backdrop dismissal is modal on purpose (the
      // version gate's update prompts): the buttons are the only way out. This
      // handler sits above Ionic's own overlay handler, which already honours
      // backdropDismiss, so without this check Back would dismiss a prompt that
      // a backdrop tap cannot. Swallow the event rather than passing it on, or
      // Back would navigate the page out from under a blocking prompt.
      if (!alert.backdropDismiss) return;
      await alert.dismiss(undefined, 'cancel');
      return;
    }

    // The "Confirming your purchase" popup is a backdropDismiss:false modal, so
    // Ionic's overlay handler would pass Back on to the page beneath it - or
    // exit the app. Back is its Close button instead: the popup goes and the
    // payment carries on being booked.
    if (this.paymentProgress.popupOpen()) {
      await this.paymentProgress.hide();
      return;
    }

    processNextHandler();
  }

  // Copyright deterrent — block the browser gestures that let a user save
  // catalog/user media to their device: right-click / long-press "Save image"
  // and drag-to-desktop. Scoped to <img>/<video> targets so context menus and
  // drag still work everywhere else. Paired with the img/video rules in
  // global.scss (-webkit-touch-callout / -webkit-user-drag) that cover the iOS
  // long-press-save sheet and WebKit image drag. Registered on document (not a
  // per-element directive) so it covers every media surface, present and future.
  // A deterrent against casual saving, NOT DRM — screenshots/devtools remain.
  private initMediaSaveGuard(): void {
    const isMedia = (target: EventTarget | null): boolean =>
      target instanceof HTMLImageElement || target instanceof HTMLVideoElement;
    document.addEventListener('contextmenu', (event) => {
      if (isMedia(event.target)) event.preventDefault();
    });
    document.addEventListener('dragstart', (event) => {
      if (isMedia(event.target)) event.preventDefault();
    });
  }

  // Keep the status bar as its own bar instead of letting the WebView draw
  // edge-to-edge under it — otherwise the Ionic header bleeds into the status
  // bar on Android (Ionic doesn't apply the top safe-area inset there). The
  // background tracks the theme: primary blue (matches the header) in light
  // mode, dark in dark mode. Light icons sit on both. The effect re-runs on
  // every theme toggle so the status bar stays in sync at runtime.
  private initStatusBar(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    StatusBar.setOverlaysWebView({ overlay: false }).catch(() => undefined);
    // Generic black/white status bar (not tied to any brand colour) so this
    // is reusable across apps: white bar + dark icons in light mode, black
    // bar + light icons in dark mode. Reacts to theme toggles at runtime.
    effect(() => {
      const dark = this.themeService.isDarkMode();
      // setBackgroundColor is Android-only; iOS ignores it (hence the catch).
      StatusBar.setBackgroundColor({
        color: dark ? '#000000' : '#ffffff',
      }).catch(() => undefined);
      // Style.Light = dark icons (for white bg); Style.Dark = light icons.
      StatusBar.setStyle({
        style: dark ? StatusBarStyle.Dark : StatusBarStyle.Light,
      }).catch(() => undefined);
    });
  }
}
